import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BarChart3, RefreshCw, TrendingUp, Download, X, Users, Loader2 } from 'lucide-react';

const StatCard = ({ label, value, sub, testId }) => (
  <div data-testid={testId} className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
    <p className="text-gray-500 text-[11px] uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>{label}</p>
    <p className="text-white text-2xl font-bold mt-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{value}</p>
    {sub && <p className="text-gray-500 text-xs mt-0.5">{sub}</p>}
  </div>
);

const StatsPanel = ({ data, loading, onRefresh, days, setDays, region, setRegion, year, setYear, onExportCsv }) => {
  if (loading && !data) {
    return <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-emerald-500 mx-auto" /></div>;
  }
  if (!data) {
    return (
      <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <BarChart3 size={32} className="text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm mb-4">Click per caricare le statistiche</p>
        <Button onClick={onRefresh} data-testid="stats-load-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <RefreshCw size={14} className="mr-2" /> CARICA STATS
        </Button>
      </div>
    );
  }

  const t = data.totals || {};
  const regByDay = data.registrations_by_day || [];
  const chatByDay = data.chat_by_day || [];
  const topRegions = data.top_regions || [];
  const bySender = data.messages_by_sender || {};
  const fc = data.finale_clicks || {};
  const yearsAvail = data.years_available || [];

  const maxRegDay = Math.max(1, ...regByDay.map(x => x.count));
  const maxChatDay = Math.max(1, ...chatByDay.map(x => x.count));
  const maxRegion = Math.max(1, ...topRegions.map(x => x.count));

  const activeFilters = [];
  if (region) activeFilters.push(`regione: ${region}`);
  if (year) activeFilters.push(`anno: ${year}`);

  return (
    <div className="space-y-4">
      {/* Header + Filtri */}
      <div className="p-4 rounded-lg border border-gray-800 space-y-3" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-white text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <TrendingUp size={18} className="text-emerald-500" /> STATISTICHE SITO
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Periodo: ultimi {data.period_days} giorni
              {activeFilters.length > 0 && <span className="text-emerald-400 ml-2">· Filtri: {activeFilters.join(' + ')}</span>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={onExportCsv}
              data-testid="stats-export-csv"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Download size={14} className="mr-1.5" /> CSV
            </Button>
            <Button onClick={onRefresh} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
              <RefreshCw size={14} />
            </Button>
          </div>
        </div>

        {/* Period selector */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500 uppercase tracking-wider font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>Periodo:</span>
          {[7, 30, 90, 365].map(d => (
            <button
              key={d}
              data-testid={`stats-period-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-full font-bold transition-colors ${
                days === d ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}>
              {d === 365 ? '1 anno' : `${d}g`}
            </button>
          ))}
        </div>

        {/* Filters: region + year */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-gray-500 text-[11px] uppercase tracking-wider mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>Filtro Regione</label>
            <div className="flex gap-1">
              <Input
                data-testid="stats-region-input"
                value={region}
                onChange={e => setRegion(e.target.value)}
                placeholder="Es. Lombardia"
                className="bg-gray-900 border-gray-700 text-white text-xs"
              />
              {region && (
                <button
                  onClick={() => setRegion('')}
                  className="px-2 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs"
                  data-testid="stats-region-clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-gray-500 text-[11px] uppercase tracking-wider mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>Filtro Anno</label>
            <div className="flex gap-1 flex-wrap">
              <button
                data-testid="stats-year-all"
                onClick={() => setYear(0)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  !year ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}>Tutti</button>
              {yearsAvail.map(y => (
                <button
                  key={y}
                  data-testid={`stats-year-${y}`}
                  onClick={() => setYear(y)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    year === y ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}>{y}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard testId="stat-registrations" label="Iscrizioni" value={t.registrations ?? 0} sub={`+${t.registrations_recent ?? 0} nel periodo`} />
        <StatCard testId="stat-contacts" label="Contatti" value={t.contacts ?? 0} />
        <StatCard testId="stat-newsletter" label="Newsletter" value={t.newsletter ?? 0} sub="Fuori Cash" />
        <StatCard testId="stat-chats" label="Chat" value={t.chats ?? 0} sub={`media ${t.messages_avg_per_chat ?? 0} msg`} />
        <StatCard testId="stat-messages" label="Messaggi" value={t.messages ?? 0} sub="totali chat" />
        <StatCard testId="stat-freestylers" label="Freestylers" value={t.freestylers_approved ?? 0} sub={`${t.freestylers_pending ?? 0} pending`} />
        <StatCard testId="stat-cypha" label="Cypha Kings" value={t.cypha_kings ?? 0} />
        <StatCard testId="stat-msg-user" label="Msg Utenti" value={bySender.user ?? 0} />
        <StatCard testId="stat-msg-bot" label="Msg Bot" value={bySender.bot ?? 0} />
        <StatCard testId="stat-msg-admin" label="Msg Admin" value={bySender.admin ?? 0} />
        <StatCard testId="stat-remind" label="Ricordamelo" value={fc.remind_total ?? 0} sub={`${fc.remind_google ?? 0} Google · ${fc.remind_ics ?? 0} .ics`} />
        <StatCard testId="stat-share" label="Condivisioni finale" value={(fc.share_whatsapp ?? 0) + (fc.share_instagram ?? 0)} sub={`${fc.share_whatsapp ?? 0} WhatsApp · ${fc.share_instagram ?? 0} Instagram`} />
      </div>

      {/* Registrations by day */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <h4 className="text-white text-sm font-bold mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          ISCRIZIONI PER GIORNO
        </h4>
        {regByDay.length === 0 ? (
          <p className="text-gray-500 text-xs py-6 text-center">Nessuna iscrizione nel periodo/filtri.</p>
        ) : (
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {regByDay.map(row => (
              <div key={row.date} data-testid={`reg-bar-${row.date}`} className="flex flex-col items-center justify-end flex-1 min-w-[28px] group">
                <span className="text-[10px] text-gray-400 mb-1 opacity-0 group-hover:opacity-100 transition">{row.count}</span>
                <div
                  className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-t transition-colors"
                  style={{ height: `${(row.count / maxRegDay) * 100}%`, minHeight: '4px' }}
                  title={`${row.date}: ${row.count}`}
                />
                <span className="text-[9px] text-gray-600 mt-1 rotate-45 origin-left whitespace-nowrap" style={{ transformOrigin: 'left top' }}>
                  {row.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat messages by day */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <h4 className="text-white text-sm font-bold mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          MESSAGGI CHAT PER GIORNO
        </h4>
        {chatByDay.length === 0 ? (
          <p className="text-gray-500 text-xs py-6 text-center">Nessun messaggio nel periodo.</p>
        ) : (
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {chatByDay.map(row => (
              <div key={row.date} className="flex flex-col items-center justify-end flex-1 min-w-[28px] group">
                <span className="text-[10px] text-gray-400 mb-1 opacity-0 group-hover:opacity-100 transition">{row.count}</span>
                <div
                  className="w-full bg-blue-600 hover:bg-blue-500 rounded-t transition-colors"
                  style={{ height: `${(row.count / maxChatDay) * 100}%`, minHeight: '4px' }}
                  title={`${row.date}: ${row.count}`}
                />
                <span className="text-[9px] text-gray-600 mt-1 rotate-45 origin-left whitespace-nowrap" style={{ transformOrigin: 'left top' }}>
                  {row.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top regions - clickable */}
      <div className="p-4 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
        <h4 className="text-white text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
          <Users size={14} className="text-red-500" /> TOP 20 REGIONI / LOCATION
          <span className="text-gray-500 text-xs font-normal">(click per filtrare)</span>
        </h4>
        {topRegions.length === 0 ? (
          <p className="text-gray-500 text-xs py-6 text-center">Nessuna location disponibile.</p>
        ) : (
          <div className="space-y-1.5">
            {topRegions.map((r, i) => (
              <button
                key={r.location}
                data-testid={`region-row-${i}`}
                onClick={() => setRegion(r.location)}
                className="w-full flex items-center gap-3 text-xs hover:bg-gray-900/60 rounded px-2 py-1 transition-colors text-left"
              >
                <span className="text-gray-500 w-6 text-right font-mono">{i + 1}.</span>
                <span className="text-white w-40 truncate capitalize">{r.location}</span>
                <div className="flex-1 bg-gray-900 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-red-600 to-red-400"
                    style={{ width: `${(r.count / maxRegion) * 100}%` }}
                  />
                </div>
                <span className="text-white font-bold w-10 text-right">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsPanel;
